<script setup lang="ts">
import type { DropZoneData } from '@/types/components/DropZoneData'
import { EHubProvider } from '@/types/enum/HubProviderEnum'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { DetectionModel } from '@/types/models/DetectionModel'
import type { AssociatedDeviceModel } from '@/types/models/DeviceModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import CheckpointPartnerInformations from '@/views/CheckpointsView/Detail/CheckpointPartnerInformations.vue'
import CheckpointClearDetectionsAction from '@/views/CheckpointsView/Detail/components/CheckpointClearDetectionsAction.vue'
import {
    BaseCard,
    IconItem,
    StackLayout,
    TitleTypography,
    TypographyItem,
    TypographyWithPrefix
} from '@rfc/rfc-components-library'
import { EColors, type DeviceDetailPageProps } from '@rfc/rfc-components-library/types'
import { type Position } from '@vueuse/core'
import { computed, inject, ref } from 'vue'
import DeviceCheckpointCard from './DeviceCheckpointCard.vue'
import DeviceDropZone from './DeviceDropZone.vue'

type Props = {
    partnerConfig?: PartnerModel
    data: CheckpointModel
    dropZoneData?: DropZoneData
    detections?: DetectionModel[]
    deletePermission?: PermissionsEnums
    editPermission?: PermissionsEnums
    deviceDetail?: DeviceDetailPageProps
    parentWidth?: number
}
const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'delete', checkpoint: CheckpointModel): void
    (e: 'click', checkpoint: CheckpointModel | undefined, event: MouseEvent): void
    (e: 'dragStart', device?: AssociatedDeviceModel, checkpointId?: number): void
    (e: 'dragEnd', position?: Position): void
    (e: 'close'): void
    (e: 'refresh'): void
    (e: 'openDrawer', device: AssociatedDeviceModel): void
    (e: 'closeDrawer'): void
}>()

const handleDeviceClick = (device: AssociatedDeviceModel) => emit('openDrawer', device)

const isViewerOnly = inject<boolean>(EHubProvider.IS_VIEWER_ONLY, false)

const devicesStackRef = ref<{ stackRef: HTMLDivElement }>()
const deviceStackWidth = computed(() =>
    devicesStackRef.value?.stackRef?.children
        ? Array.from(devicesStackRef.value?.stackRef?.children).reduce(
              (prev, current) => prev + current.getBoundingClientRect().width,
              0
          )
        : 0
)
const devicesWidthOverParent = computed(() => deviceStackWidth.value > (props.parentWidth ?? 0))
</script>

<template>
    <DeviceDropZone
        :dropZoneData="dropZoneData"
        :checkpoint="data"
        :hasDevices="devicesWidthOverParent"
        @close="$emit('close')"
        @refresh="$emit('refresh')"
        v-slot="props">
        <BaseCard
            class="flex checkpoint-card pointer"
            @click="$emit('click', data, $event)"
            v-bind="props">
            <template #header>
                <StackLayout class="header-checkpoint-card align-items-center space-between">
                    <StackLayout direction="column">
                        <TitleTypography :title="data?.name" class="actual-text" />
                        <StackLayout direction="column" class="subtitle-checkpoint">
                            <CheckpointPartnerInformations
                                v-if="partnerConfig"
                                :partnerConfig="partnerConfig"
                                :partnerConfigValue="data.partner_config" />
                            <TypographyWithPrefix
                                :prefix="$t('event.checkpoint.detections')"
                                :label="data?.detections_stats?.nb_detections" />
                        </StackLayout>
                    </StackLayout>
                    <StackLayout gap="4" class="align-items-center">
                        <CheckpointClearDetectionsAction
                            v-if="isViewerOnly"
                            :data="data"
                            asIcon
                            @refresh="$emit('refresh')" />
                        <IconItem
                            icon="cog"
                            :type="EColors.PRIMARY"
                            class="hover:rotate-270 duration-500 ease-in-out" />
                    </StackLayout>
                </StackLayout>
            </template>
            <template #content>
                <StackLayout class="checkpoint-card-content">
                    <StackLayout
                        v-if="data.associates?.length"
                        ref="devicesStackRef"
                        :gap="16"
                        :class="`devices-wrapper ${devicesWidthOverParent ? '' : 'flex-nowrap'}`">
                        <StackLayout
                            v-for="(device, key) in data.associates"
                            :key="key"
                            isResponsiveSmallScreen>
                            <BaseCard
                                v-if="isViewerOnly"
                                class="primary-border device-card fit-content cursor-default device-cp-drag-card">
                                <template #content>
                                    <TypographyItem
                                        v-if="device.device_id || device.id"
                                        :label="device.name" />
                                </template>
                            </BaseCard>
                            <DeviceCheckpointCard
                                v-else
                                :detections="detections"
                                :device="device"
                                :eventId="data?.event_id"
                                :checkpointId="data?.id"
                                @click="handleDeviceClick(device)"
                                @dragStart="$emit('dragStart', $event, data.id)"
                                @dragEnd="$emit('dragEnd', $event)" />
                        </StackLayout>
                    </StackLayout>
                    <TypographyItem v-else :label="$t('event.checkpoint.noDeviceAssociated')" />
                </StackLayout>
            </template>
        </BaseCard>
    </DeviceDropZone>
</template>

<style scoped lang="scss">
.devices-wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
}

@media (max-width: 768px) {
    .devices-wrapper {
        flex-direction: column;
        align-items: center;
    }
}

.detection-class {
    & :deep(.no-detection) {
        white-space: nowrap;
    }
}

.header-checkpoint-card {
    padding: 0.3em 1.5em 0.8em;
    border-bottom: 1px solid grey;
    min-height: 55px;
}
</style>
